import {
  Button,
  Card,
  Container,
  makeStyles,
  Typography
} from "@material-ui/core";
import { useAppConfigUpdate } from "@saleor/appConfiguration/mutations";
import { AppConfig_appConfig } from "@saleor/appConfiguration/types/AppConfig";
import {
  appConfigurationPath,
  appConfigurationUrl,
  AppConfigurationUrlDialog,
  AppConfigurationUrlQueryParams
} from "@saleor/appConfiguration/url";
import AppHeader from "@saleor/components/AppHeader";
import PageHeader from "@saleor/components/PageHeader";
import Skeleton from "@saleor/components/Skeleton";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages, sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import * as React from "react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Moment from "react-moment";

import AppConfigDetailList from "../AppConfigDetailList";
import AppConfigUploadDialog from "../AppConfigUploadDialog";

interface AppConfigDetailProps extends ListProps {
  appConfig: AppConfig_appConfig;
  refetch: () => void;
  params: AppConfigurationUrlQueryParams;
  loading: boolean;
}

const useStyles = makeStyles(
  () => ({
    createdAt: {
      display: "flex",
      fontWeight: 600,
      gridColumnGap: "4px",
      gridTemplateColumns: "1fr",
      marginBottom: "20px",
      marginTop: "-25px"
    }
  }),
  { name: "AppConfigDetail" }
);

const AppConfigDetail: React.FC<AppConfigDetailProps> = props => {
  const {
    appConfig,
    params,
    refetch,
    loading,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    pageInfo,
    settings
  } = props;
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [uploadLoading, setUploadLoading] = useState(false);

  const classes = useStyles(props);

  const [openModal, closeModal] = createDialogActionHandlers<
    AppConfigurationUrlDialog,
    AppConfigurationUrlQueryParams
  >(navigate, params => appConfigurationUrl(appConfig.id, params), params);

  const [appConfigUpdate, appConfigUpdateOpts] = useAppConfigUpdate({
    onCompleted: data => {
      if (data.appConfigUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Update success",
            id: "menuListCreatedMenu"
          })
        });
      } else if (
        data.appConfigUpdate?.errors[0].code === "INVALID" &&
        data.appConfigUpdate?.errors[0].field === "version"
      ) {
        notify({
          status: "warning",
          text: data?.appConfigUpdate?.errors[0].message
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      }
      refetch();
      setUploadLoading(false);
      closeModal();
    }
  });

  return (
    <>
      <Container>
        <AppHeader onBack={() => navigate(appConfigurationPath)}>
          {intl.formatMessage(sectionNames.appConfiguration)}
        </AppHeader>
        <PageHeader title={appConfig?.name}>
          <Button
            id="uploadBtn"
            color="primary"
            variant="contained"
            onClick={() => openModal("update")}
          >
            <FormattedMessage defaultMessage="Upload" description="button" />
          </Button>
        </PageHeader>
        <Typography className={classes.createdAt} id="createdAtValue">
          Created at:
          {appConfig && appConfig.createdAt ? (
            <Moment format="MM/DD/YYYY" tz="Asia/Manila">
              {appConfig?.updatedAt}
            </Moment>
          ) : (
            <Skeleton />
          )}
        </Typography>
        <Card>
          <AppConfigDetailList
            appConfig={maybe(() =>
              appConfig.appConfigs.edges.map(edges => edges.node)
            )}
            settings={settings}
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
            onUpdateListSettings={onUpdateListSettings}
            pageInfo={pageInfo}
            disabled={loading}
            onRowClick={() => undefined}
          />
        </Card>
      </Container>

      <AppConfigUploadDialog
        open={params.action === "update"}
        params={params.action}
        loading={appConfigUpdateOpts.loading || uploadLoading}
        onFileError={() => {
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage: "Please upload JSON files only."
            }),
            title: intl.formatMessage({
              defaultMessage: "Can't upload file!",
              description: "import, header"
            })
          });
        }}
        confirmButtonState={"loading"}
        disabled={loading}
        errors={[]}
        onClose={closeModal}
        onSave={formdata => {
          appConfigUpdate({
            variables: { id: appConfig.id, json: formdata.json }
          });
        }}
      />
    </>
  );
};

export default AppConfigDetail;
